import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    StyledTreatmentRequestCode,
    StyledTreatmentRequestDetailsHeader,
    StyledTreatmentRequestHeroSectionBody
} from './style'
import {Trans, useTranslation} from '@/translations/i18n'
import {formatDate} from '@/dayjs'
import {TreatmentRequestSmall, TreatmentRequestExtended} from '@/features/treatment-requests/types'
import {TreatmentRequestDetailsOptions} from '@/features/treatment-requests/components/treatment-request-details-options/TreatmentRequestDetailsOptions'
import {TreatmentRequestDetailsStatus} from '@/features/treatment-requests/components/treatment-request-details-status/TreatmentRequestDetailsStatus'
import {TreatmentRequestFollowUpModal} from '@/features/treatment-requests/components/treatment-request-follow-up-modal/TreatmentRequestFollowUpModal'
import {Badge} from '@/components/ui/badge/Badge'
import {TratmentRequestTherapistDoctorSelector} from '../tratment-request-therapist-doctor-selector/TratmentRequestTherapistDoctorSelector'
import {Button} from '@/components/ui/button/Button'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {useTheme} from 'styled-components'
import {Text} from '@/components/ui/text/Text'
import {useOutletContext} from 'react-router-dom'
import {usePermissions} from '@/features/auth/store/store'

export const TreatmentRequestHeroSection = () => {
    const {t} = useTranslation()
    const {treatmentRequest} = useOutletContext<{treatmentRequest: TreatmentRequestExtended}>()
    const {code, createdAt} = treatmentRequest
    const theme = useTheme()
    const therapists = treatmentRequest.therapists ?? []
    const permissions = usePermissions()

    return (
        <Flexbox direction="column" fullWidth gap={4}>
            <StyledTreatmentRequestDetailsHeader
                patientId={treatmentRequest.patientId}
                endSlot={<TreatmentRequestDetailsOptions treatmentRequest={treatmentRequest} />}
                subtitle={t('treatment_requests:request_date_value', {value: formatDate(createdAt, 'DD/MM/YYYY')})}
                title={
                    <Trans
                        i18nKey="treatment_requests:treatment_request_n"
                        values={{number: code}}
                        components={{span: <StyledTreatmentRequestCode />}}
                    />
                }
            />
            <StyledTreatmentRequestHeroSectionBody gap={2} align="center">
                <TreatmentRequestDetailsStatus treatmentRequest={TreatmentRequestSmall.parse(treatmentRequest)} />
                {treatmentRequest.status == 'waiting_list' && treatmentRequest.expirationDate && (
                    <Flexbox gap={2} align="center">
                        <TreatmentRequestFollowUpModal
                            isReadOnly={!permissions?.requests?.write}
                            followUps={treatmentRequest.followups}
                            expirationDate={treatmentRequest.expirationDate}
                        />
                        <Badge variant="neutral" size="lg" shape="rectangle">
                            {t('treatment_requests:expiration_date_value', {
                                date: formatDate(treatmentRequest.expirationDate, 'DD/MM/YYYY')
                            })}
                        </Badge>
                    </Flexbox>
                )}

                {treatmentRequest.status == 'in_progress' && (
                    <>
                        <TratmentRequestTherapistDoctorSelector
                            treatmentRequest={treatmentRequest}
                            group="referring_doctor"
                            isReadOnly={!permissions?.requests?.write}
                            trigger={() => (
                                <Button variant="secondary" size="sm">
                                    {treatmentRequest.referringDoctor == null ? (
                                        <Text size="textSm">
                                            <Trans
                                                i18nKey="treatment_requests:referring_doctor_name"
                                                values={{name: t('commons:none')}}
                                                components={[<span style={{textDecoration: 'underline'}} />]}
                                            />
                                        </Text>
                                    ) : (
                                        <>
                                            <Avatar size={'xs'} name={treatmentRequest.referringDoctor.fullName} />
                                            <Text size="textSm">
                                                <Trans
                                                    i18nKey="treatment_requests:referring_doctor_name"
                                                    values={{name: treatmentRequest.referringDoctor.fullName}}
                                                    components={[<span />]}
                                                />
                                            </Text>
                                        </>
                                    )}
                                </Button>
                            )}
                        />

                        <TratmentRequestTherapistDoctorSelector
                            treatmentRequest={treatmentRequest}
                            group="therapist"
                            isReadOnly={!permissions?.requests?.write}
                            trigger={() => (
                                <Button variant="secondary" size="sm">
                                    {therapists.length == 0 ? (
                                        <Text size="textSm">
                                            <Trans
                                                i18nKey="treatment_requests:therapist_name"
                                                values={{name: t('commons:none')}}
                                                components={[<span style={{textDecoration: 'underline'}} />]}
                                            />
                                        </Text>
                                    ) : (
                                        <>
                                            {therapists.slice(0, 2).map((therapist, index) => (
                                                <Avatar
                                                    key={therapist.id}
                                                    style={
                                                        index > 0
                                                            ? {marginLeft: -theme.spacing * 4}
                                                            : {position: 'relative'}
                                                    }
                                                    size={'xs'}
                                                    name={therapist.fullName}
                                                />
                                            ))}
                                            <Text size="textSm">
                                                {therapists.length >= 2 ? (
                                                    t('treatment_requests:x_therapists', {count: therapists.length})
                                                ) : (
                                                    <Trans
                                                        i18nKey="treatment_requests:therapist_name"
                                                        values={{name: therapists[0].fullName}}
                                                        components={[<span />]}
                                                    />
                                                )}
                                            </Text>
                                        </>
                                    )}
                                </Button>
                            )}
                        />
                    </>
                )}
            </StyledTreatmentRequestHeroSectionBody>
        </Flexbox>
    )
}
